import { c as _c } from "react/compiler-runtime";
import { getOrgAppearencesOptions } from '@onefootprint/fetch-dashboard';
import { IcoPaint16 } from '@onefootprint/icons';
import type { CursorPaginatedAppearence, GraphOperationUpdateSettings } from '@onefootprint/request-types/dashboard';
import { Dialog, Form, IconButton } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Link from '@/components/link';
type PlaybookAppearanceFormData = {
  appearanceId: string;
};
export type PlaybookAppearanceProps = {
  onSubmit?: (data: Omit<GraphOperationUpdateSettings, 'kind'>) => Promise<void>;
  defaultValues?: PlaybookAppearanceFormData;
};
const PlaybookAppearance = ({
  onSubmit,
  defaultValues
}: PlaybookAppearanceProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'themePicker'
  });
  const appearanceQuery = useQuery(getOrgAppearencesOptions());
  const [open, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasCustomTheme = !!defaultValues?.appearanceId;
  const handleSubmit = async (data: Omit<GraphOperationUpdateSettings, 'kind'>) => {
    setIsSubmitting(true);
    await onSubmit?.(data);
    setIsSubmitting(false);
    setIsOpen(false);
  };
  const handleClose = () => {
    setIsOpen(false);
  };
  const handleReset = async () => {
    try {
      setIsSubmitting(true);
      // @ts-expect-error
      await onSubmit?.({
        appearanceId: null
      });
      setIsOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <>
      <IconButton icon={IcoPaint16} aria-label={t($ => $.trigger)} onClick={() => setIsOpen(true)} size="compact" variant="outline" />
      <Dialog title={t($_0 => $_0.title)} open={open} onClose={handleClose} isConfirmation primaryButton={{
      form: 'theme-picker',
      label: t($_1 => $_1.save),
      type: 'submit',
      disabled: !onSubmit,
      loading: isSubmitting
    }} secondaryButton={{
      label: t($_2 => $_2.cancel),
      onClick: handleClose,
      disabled: isSubmitting
    }} linkButton={hasCustomTheme ? {
      label: t($_3 => $_3.useDefault),
      onClick: handleReset,
      disabled: !onSubmit || isSubmitting
    } : undefined}>
        {appearanceQuery.data && <PlaybookAppearanceForm appearanceList={appearanceQuery.data} onSubmit={onSubmit ? handleSubmit : undefined} defaultValues={defaultValues} />}
      </Dialog>
    </>;
};
type PlaybookAppearanceFormProps = {
  defaultValues?: PlaybookAppearanceFormData;
  appearanceList: CursorPaginatedAppearence;
  onSubmit?: (data: Omit<GraphOperationUpdateSettings, 'kind'>) => Promise<void>;
};
const PlaybookAppearanceForm = t0 => {
  const $ = _c(13);
  const {
    defaultValues,
    appearanceList: t1,
    onSubmit
  } = t0;
  const {
    data
  } = t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "themePicker.form"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t2);
  const t3 = defaultValues?.appearanceId || "";
  let t4;
  if ($[1] !== t3) {
    t4 = {
      defaultValues: {
        appearanceId: t3
      }
    };
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const formMethods = useForm(t4);
  const {
    handleSubmit
  } = formMethods;
  let t5;
  if ($[3] !== data || $[4] !== onSubmit) {
    t5 = async formData => {
      const appearance_0 = data.find(appearance => appearance.id === formData.appearanceId);
      if (!appearance_0) {
        return;
      }
      await onSubmit?.({
        appearanceId: appearance_0.id
      });
    };
    $[3] = data;
    $[4] = onSubmit;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleFormSubmit = t5;
  let t6;
  if ($[6] !== data || $[7] !== formMethods || $[8] !== handleFormSubmit || $[9] !== handleSubmit || $[10] !== onSubmit || $[11] !== t) {
    t6 = <>{data.length === 0 ? <div className="gap-0.5 align-center max-md:text-center md:flex md:justify-center"><span className="text-body-2 text-primary">{t(_temp)}</span>{" "}<Link className="text-body-2" href="/developers/appearance" prefetch={true} rel="noopener noreferrer" target="_blank">{t(_temp2)}</Link></div> : <FormProvider {...formMethods}><form onSubmit={handleSubmit(handleFormSubmit)} id="theme-picker"><div className="flex flex-col gap-4"><div className="text-body-2 text-primary">{t(_temp3)}</div><Form.Select label={t(_temp4)} name="appearanceId" options={data.map(_temp5)} placeholder={t(_temp6)} disabled={onSubmit === undefined} rules={{
              required: {
                message: t(_temp7),
                value: true
              }
            }} size="compact" /></div></form></FormProvider>}</>;
    $[6] = data;
    $[7] = formMethods;
    $[8] = handleFormSubmit;
    $[9] = handleSubmit;
    $[10] = onSubmit;
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
export default PlaybookAppearance;
function _temp($) {
  return $.emptyState.description;
}
function _temp2($_0) {
  return $_0.emptyState.cta;
}
function _temp3($_1) {
  return $_1.title;
}
function _temp4($_2) {
  return $_2.theme.label;
}
function _temp5(appearance_1) {
  return {
    value: appearance_1.id,
    label: appearance_1.name || "Unnamed Theme"
  };
}
function _temp6($_3) {
  return $_3.theme.placeholder;
}
function _temp7($_4) {
  return $_4.theme.errors.required;
}